'use client';

import { useState, useEffect, Suspense, useCallback } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import PublicNavigation from '@/components/common/PublicNavigation';
import CollapsibleFilterPanel from './CollapsibleFilterPanel';
import ImageGridHeader from './ImageGridHeader';
import ImageGridItem from './ImageGridItem';
import ImageGridSkeleton from './ImageGridSkeleton';
import ImageGridEmpty from './ImageGridEmpty';
import { entryService } from '@/services/entryService';
import { supabase } from '@/lib/supabase';
import { objectPositionFromCrop } from '@/lib/images/objectPosition';

interface ImageEntry {
  id: string;
  title: string;
  image: string;
  alt: string;
  mode: string;
  publishDate: string;
  subject: string;
  subSubject?: string;
  tone?: string[];
  intent?: string[];
  imageMonochrome?: boolean;
  projectName?: string;
  seriesName?: string;
  objectPosition?: string;
}

// Mock data removed - now fetching from database


function ImagesGridContent() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const [isHydrated, setIsHydrated] = useState(false);
  const [filteredEntries, setFilteredEntries] = useState<ImageEntry[]>([]);
  const [allEntries, setAllEntries] = useState<ImageEntry[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const formatDateSafe = (value?: string | null) => {
    if (!value) return '';
    const dateStr = value.length === 10 ? `${value}T12:00:00Z` : value;
    const d = new Date(dateStr);
    if (Number.isNaN(d.getTime())) return '';
    return d.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });
  };

  const applyFilters = useCallback((entries: ImageEntry[]) => {
    const typeFilter = searchParams?.get('type')?.split(',') || [];
    const categoryFilter = searchParams?.get('category')?.split(',') || [];
    const tagFilter = searchParams?.get('tag')?.split(',') || [];
    const yearFilter = searchParams?.get('year')?.split(',') || [];

    let filtered = entries;

      if (typeFilter.length > 0) {
        filtered = filtered.filter((entry) =>
        typeFilter.some((type) => entry.mode.toLowerCase() === type.toLowerCase())
        );
      }

      if (categoryFilter.length > 0) {
        filtered = filtered.filter((entry) =>
        categoryFilter.some(
          (cat) =>
          entry.subject.toLowerCase().includes(cat.toLowerCase()) ||
          entry.subSubject?.toLowerCase().includes(cat.toLowerCase())
        )
        );
      }

      if (tagFilter.length > 0) {
        filtered = filtered.filter((entry) =>
        tagFilter.some(
          (tag) =>
          entry.tone?.some((t) => t.toLowerCase().includes(tag.toLowerCase())) ||
          entry.intent?.some((i) => i.toLowerCase().includes(tag.toLowerCase()))
        )
        );
      }

      if (yearFilter.length > 0) {
        filtered = filtered.filter((entry) => {
          const entryYear = new Date(entry.publishDate).getFullYear().toString();
          return yearFilter.includes(entryYear);
        });
      }

      setFilteredEntries(filtered);
  }, [searchParams]);

  const loadImages = useCallback(async () => {
    setIsLoading(true);
    try {
      // Fetch published image entries
      const { data: entries, error } = await entryService.getAllPublished('image');
      
      if (error || !entries) {
        console.error('Error loading images:', error);
        setAllEntries([]);
        setFilteredEntries([]);
        setIsLoading(false);
        return;
      }

      // Fetch subjects for all entries
      const entryIds = entries.map(e => e.id);
      const [subjectsResult, tonesResult, intentsResult] = await Promise.all([
        supabase
          .from('entry_subjects')
          .select('entry_id, subject_id, sub_subject_id')
          .in('entry_id', entryIds),
        supabase
          .from('entry_tones')
          .select('entry_id, tone')
          .in('entry_id', entryIds),
        supabase
          .from('entry_intents')
          .select('entry_id, intent')
          .in('entry_id', entryIds),
      ]);

      // Create maps for subjects, tones, intents
      const subjectMap = new Map<string, { subject: string; subSubject?: string }>();
        if (subjectsResult.data) {
          const subjectIds = Array.from(new Set(subjectsResult.data.map((s) => s.subject_id).filter(Boolean)));
        const { data: subjectNames } = await supabase
          .from('subjects')
          .select('id, name')
          .in('id', subjectIds);
        
        const subSubjectIds = Array.from(new Set(subjectsResult.data.map((s) => s.sub_subject_id).filter(Boolean)));
        const { data: subSubjectNames } = await supabase
          .from('sub_subjects')
          .select('id, name')
          .in('id', subSubjectIds);

        if (subjectNames) {
          const subjectIdToName = new Map(subjectNames.map(s => [s.id, s.name]));
          const subSubjectIdToName = new Map(subSubjectNames?.map(s => [s.id, s.name]) || []);
          
          subjectsResult.data.forEach(es => {
            const subjectName = subjectIdToName.get(es.subject_id);
            const subSubjectName = es.sub_subject_id ? subSubjectIdToName.get(es.sub_subject_id) : undefined;
            if (subjectName) {
              subjectMap.set(es.entry_id, { subject: subjectName, subSubject: subSubjectName });
            }
          });
        }
      }

      const toneMap = new Map<string, string[]>();
      if (tonesResult.data) {
        tonesResult.data.forEach(et => {
          const current = toneMap.get(et.entry_id) || [];
          toneMap.set(et.entry_id, [...current, et.tone]);
        });
      }

      const intentMap = new Map<string, string[]>();
      if (intentsResult.data) {
        intentsResult.data.forEach(ei => {
          const current = intentMap.get(ei.entry_id) || [];
          intentMap.set(ei.entry_id, [...current, ei.intent]);
        });
      }

      // Transform entries to ImageEntry format
      const imageEntries: ImageEntry[] = entries.map((entry) => {
        const mode = entry.image_mode || 'photo';
        const subjectData = subjectMap.get(entry.id);
        
        return {
          id: entry.id,
          title: entry.title,
          image: entry.coverImageUrl || '/assets/images/no_image.png',
          alt: entry.title,
          mode: mode.charAt(0).toUpperCase() + mode.slice(1),
          publishDate: formatDateSafe(entry.publish_date || entry.publishedAt || entry.createdAt),
          subject: subjectData?.subject || '',
          subSubject: subjectData?.subSubject,
          tone: toneMap.get(entry.id)?.map(t => t.charAt(0).toUpperCase() + t.slice(1)),
          intent: intentMap.get(entry.id)?.map(i => i.charAt(0).toUpperCase() + i.slice(1)),
          imageMonochrome: (entry.metadata?.coverImageMonochrome as boolean) || false,
          projectName: entry.projectId ? undefined : undefined, // TODO: Fetch project name
          seriesName: entry.series || undefined,
          objectPosition: objectPositionFromCrop(entry.crops?.['16:9'], { x: entry.focal_x, y: entry.focal_y }),
        };
      });

      setAllEntries(imageEntries);
      applyFilters(imageEntries);
    } catch (error) {
      console.error('Error loading images:', error);
      setAllEntries([]);
      setFilteredEntries([]);
    } finally {
      setIsLoading(false);
    }
  }, [applyFilters]);

  useEffect(() => {
    setIsHydrated(true);
    loadImages();
  }, [loadImages]);

  useEffect(() => {
    if (!isHydrated || allEntries.length === 0) return;
    applyFilters(allEntries);
  }, [applyFilters, isHydrated, allEntries]);

  const hasActiveFilters = () => {
    if (!isHydrated) return false;
    return (
      searchParams.get('type') !== null ||
      searchParams.get('category') !== null ||
      searchParams.get('tag') !== null ||
      searchParams.get('year') !== null);

  };

  const handleClearFilters = () => {
    router.push('/photography-grid', { scroll: false });
  };

  if (!isHydrated) {
    return (
      <div data-component-id="src/app/photography-grid/components/ImagesGridInteractive.tsx:231:6" data-component-path="src/app/photography-grid/components/ImagesGridInteractive.tsx" data-component-line="231" data-component-end-line="246" data-component-file="ImagesGridInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22min-h-screen%20bg-background%22%7D" className="min-h-screen bg-background">
        <PublicNavigation data-component-id="src/app/photography-grid/components/ImagesGridInteractive.tsx:232:8" data-component-path="src/app/photography-grid/components/ImagesGridInteractive.tsx" data-component-line="232" data-component-end-line="232" data-component-file="ImagesGridInteractive.tsx" data-component-name="PublicNavigation" data-component-content="%7B%22elementName%22%3A%22PublicNavigation%22%7D" />
        <div data-component-id="src/app/photography-grid/components/ImagesGridInteractive.tsx:233:8" data-component-path="src/app/photography-grid/components/ImagesGridInteractive.tsx" data-component-line="233" data-component-end-line="245" data-component-file="ImagesGridInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22pt-20%22%7D" className="pt-20">
          <main data-component-id="src/app/photography-grid/components/ImagesGridInteractive.tsx:234:10" data-component-path="src/app/photography-grid/components/ImagesGridInteractive.tsx" data-component-line="234" data-component-end-line="244" data-component-file="ImagesGridInteractive.tsx" data-component-name="main" data-component-content="%7B%22elementName%22%3A%22main%22%2C%22className%22%3A%22max-w-7xl%20mx-auto%20px-6%20lg%3Apx-8%20py-12%22%7D" className="max-w-7xl mx-auto px-6 lg:px-8 py-12">
            <div data-component-id="src/app/photography-grid/components/ImagesGridInteractive.tsx:235:12" data-component-path="src/app/photography-grid/components/ImagesGridInteractive.tsx" data-component-line="235" data-component-end-line="242" data-component-file="ImagesGridInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22mb-8%22%7D" className="mb-8">
              <h1 data-component-id="src/app/photography-grid/components/ImagesGridInteractive.tsx:236:14" data-component-path="src/app/photography-grid/components/ImagesGridInteractive.tsx" data-component-line="236" data-component-end-line="238" data-component-file="ImagesGridInteractive.tsx" data-component-name="h1" data-component-content="%7B%22elementName%22%3A%22h1%22%2C%22className%22%3A%22font-heading%20font-bold%20text-4xl%20text-foreground%20mb-2%22%2C%22textContent%22%3A%22Images%22%7D" className="font-heading font-bold text-4xl text-foreground mb-2">
                Images
              </h1>
              <p data-component-id="src/app/photography-grid/components/ImagesGridInteractive.tsx:239:14" data-component-path="src/app/photography-grid/components/ImagesGridInteractive.tsx" data-component-line="239" data-component-end-line="241" data-component-file="ImagesGridInteractive.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-muted-foreground%22%2C%22textContent%22%3A%22Visual%20content%20and%20photography%22%7D" className="text-muted-foreground">
                Visual content and photography
              </p>
            </div>
            <ImageGridSkeleton data-component-id="src/app/photography-grid/components/ImagesGridInteractive.tsx:243:12" data-component-path="src/app/photography-grid/components/ImagesGridInteractive.tsx" data-component-line="243" data-component-end-line="243" data-component-file="ImagesGridInteractive.tsx" data-component-name="ImageGridSkeleton" data-component-content="%7B%22elementName%22%3A%22ImageGridSkeleton%22%7D" />
          </main>
        </div>
      </div>
    );
  }

  return (
    <div data-component-id="src/app/photography-grid/components/ImagesGridInteractive.tsx:251:4" data-component-path="src/app/photography-grid/components/ImagesGridInteractive.tsx" data-component-line="251" data-component-end-line="306" data-component-file="ImagesGridInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22min-h-screen%20bg-background%22%7D" className="min-h-screen bg-background">
      <PublicNavigation data-component-id="src/app/photography-grid/components/ImagesGridInteractive.tsx:252:6" data-component-path="src/app/photography-grid/components/ImagesGridInteractive.tsx" data-component-line="252" data-component-end-line="252" data-component-file="ImagesGridInteractive.tsx" data-component-name="PublicNavigation" data-component-content="%7B%22elementName%22%3A%22PublicNavigation%22%7D" />
      <div data-component-id="src/app/photography-grid/components/ImagesGridInteractive.tsx:253:6" data-component-path="src/app/photography-grid/components/ImagesGridInteractive.tsx" data-component-line="253" data-component-end-line="305" data-component-file="ImagesGridInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22pt-20%22%7D" className="pt-20">
        <main data-component-id="src/app/photography-grid/components/ImagesGridInteractive.tsx:254:8" data-component-path="src/app/photography-grid/components/ImagesGridInteractive.tsx" data-component-line="254" data-component-end-line="304" data-component-file="ImagesGridInteractive.tsx" data-component-name="main" data-component-content="%7B%22elementName%22%3A%22main%22%2C%22className%22%3A%22max-w-7xl%20mx-auto%20px-6%20lg%3Apx-8%20py-12%22%7D" className="max-w-7xl mx-auto px-6 lg:px-8 py-12">
          <Suspense
            fallback={
              <div data-component-id="src/app/photography-grid/components/ImagesGridInteractive.tsx:257:14" data-component-path="src/app/photography-grid/components/ImagesGridInteractive.tsx" data-component-line="257" data-component-end-line="261" data-component-file="ImagesGridInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22mb-6%20bg-card%20rounded-lg%20border%20border-border%20shadow-editorial-sm%22%7D" className="mb-6 bg-card rounded-lg border border-border shadow-editorial-sm">
                <div data-component-id="src/app/photography-grid/components/ImagesGridInteractive.tsx:258:16" data-component-path="src/app/photography-grid/components/ImagesGridInteractive.tsx" data-component-line="258" data-component-end-line="260" data-component-file="ImagesGridInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22px-6%20py-4%20border-b%20border-border%22%7D" className="px-6 py-4 border-b border-border">
                  <div data-component-id="src/app/photography-grid/components/ImagesGridInteractive.tsx:259:18" data-component-path="src/app/photography-grid/components/ImagesGridInteractive.tsx" data-component-line="259" data-component-end-line="259" data-component-file="ImagesGridInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22h-6%20w-32%20bg-muted%20rounded%20animate-pulse%22%7D" className="h-6 w-32 bg-muted rounded animate-pulse" />
                </div>
              </div>
            }
          >
            <CollapsibleFilterPanel data-component-id="src/app/photography-grid/components/ImagesGridInteractive.tsx:264:12" data-component-path="src/app/photography-grid/components/ImagesGridInteractive.tsx" data-component-line="264" data-component-end-line="264" data-component-file="ImagesGridInteractive.tsx" data-component-name="CollapsibleFilterPanel" data-component-content="%7B%22elementName%22%3A%22CollapsibleFilterPanel%22%7D" />
          </Suspense>

          <ImageGridHeader data-component-id="src/app/photography-grid/components/ImagesGridInteractive.tsx:267:10" data-component-path="src/app/photography-grid/components/ImagesGridInteractive.tsx" data-component-line="267" data-component-end-line="272" data-component-file="ImagesGridInteractive.tsx" data-component-name="ImageGridHeader" data-component-content="%7B%22elementName%22%3A%22ImageGridHeader%22%7D"
            totalCount={allEntries.length}
            filteredCount={filteredEntries.length}
            hasActiveFilters={hasActiveFilters()}
            onClearFilters={handleClearFilters}
          />

          {isLoading ? (
            <ImageGridSkeleton data-component-id="src/app/photography-grid/components/ImagesGridInteractive.tsx:275:12" data-component-path="src/app/photography-grid/components/ImagesGridInteractive.tsx" data-component-line="275" data-component-end-line="275" data-component-file="ImagesGridInteractive.tsx" data-component-name="ImageGridSkeleton" data-component-content="%7B%22elementName%22%3A%22ImageGridSkeleton%22%7D" />
          ) : filteredEntries.length > 0 ? (
            <div data-component-id="src/app/photography-grid/components/ImagesGridInteractive.tsx:277:12" data-component-path="src/app/photography-grid/components/ImagesGridInteractive.tsx" data-component-line="277" data-component-end-line="297" data-component-file="ImagesGridInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22grid%20grid-cols-1%20md%3Agrid-cols-2%20gap-6%22%7D" className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {filteredEntries.map((entry) => (
                <ImageGridItem data-component-id="src/app/photography-grid/components/ImagesGridInteractive.tsx:279:16" data-component-path="src/app/photography-grid/components/ImagesGridInteractive.tsx" data-component-line="279" data-component-end-line="295" data-component-file="ImagesGridInteractive.tsx" data-component-name="ImageGridItem" data-component-content="%7B%22elementName%22%3A%22ImageGridItem%22%7D"
                  key={entry.id}
                  id={entry.id}
                  title={entry.title}
                  image={entry.image}
                  alt={entry.alt}
                  mode={entry.mode}
                  publishDate={entry.publishDate}
                  imageMonochrome={entry.imageMonochrome}
                  subject={entry.subject}
                  subSubject={entry.subSubject}
                  tone={entry.tone}
                  intent={entry.intent}
                  projectName={entry.projectName}
                  seriesName={entry.seriesName}
                  objectPosition={entry.objectPosition}
                />
              ))}
            </div>
          ) : (
            <ImageGridEmpty data-component-id="src/app/photography-grid/components/ImagesGridInteractive.tsx:299:12" data-component-path="src/app/photography-grid/components/ImagesGridInteractive.tsx" data-component-line="299" data-component-end-line="302" data-component-file="ImagesGridInteractive.tsx" data-component-name="ImageGridEmpty" data-component-content="%7B%22elementName%22%3A%22ImageGridEmpty%22%7D"
              hasActiveFilters={hasActiveFilters()}
              onClearFilters={handleClearFilters}
            />
          )}
        </main>
      </div>
    </div>
  );

}

const ImagesGridInteractive = () => {
  return (
    <Suspense
      fallback={
        <div data-component-id="src/app/photography-grid/components/ImagesGridInteractive.tsx:315:8" data-component-path="src/app/photography-grid/components/ImagesGridInteractive.tsx" data-component-line="315" data-component-end-line="322" data-component-file="ImagesGridInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22min-h-screen%20bg-background%22%7D" className="min-h-screen bg-background">
          <PublicNavigation data-component-id="src/app/photography-grid/components/ImagesGridInteractive.tsx:316:10" data-component-path="src/app/photography-grid/components/ImagesGridInteractive.tsx" data-component-line="316" data-component-end-line="316" data-component-file="ImagesGridInteractive.tsx" data-component-name="PublicNavigation" data-component-content="%7B%22elementName%22%3A%22PublicNavigation%22%7D" />
          <div data-component-id="src/app/photography-grid/components/ImagesGridInteractive.tsx:317:10" data-component-path="src/app/photography-grid/components/ImagesGridInteractive.tsx" data-component-line="317" data-component-end-line="321" data-component-file="ImagesGridInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22pt-20%22%7D" className="pt-20">
            <main data-component-id="src/app/photography-grid/components/ImagesGridInteractive.tsx:318:12" data-component-path="src/app/photography-grid/components/ImagesGridInteractive.tsx" data-component-line="318" data-component-end-line="320" data-component-file="ImagesGridInteractive.tsx" data-component-name="main" data-component-content="%7B%22elementName%22%3A%22main%22%2C%22className%22%3A%22max-w-7xl%20mx-auto%20px-6%20lg%3Apx-8%20py-12%22%7D" className="max-w-7xl mx-auto px-6 lg:px-8 py-12">
              <ImageGridSkeleton data-component-id="src/app/photography-grid/components/ImagesGridInteractive.tsx:319:14" data-component-path="src/app/photography-grid/components/ImagesGridInteractive.tsx" data-component-line="319" data-component-end-line="319" data-component-file="ImagesGridInteractive.tsx" data-component-name="ImageGridSkeleton" data-component-content="%7B%22elementName%22%3A%22ImageGridSkeleton%22%7D" />
            </main>
          </div>
        </div>
      }
    >
      <ImagesGridContent data-component-id="src/app/photography-grid/components/ImagesGridInteractive.tsx:325:6" data-component-path="src/app/photography-grid/components/ImagesGridInteractive.tsx" data-component-line="325" data-component-end-line="325" data-component-file="ImagesGridInteractive.tsx" data-component-name="ImagesGridContent" data-component-content="%7B%22elementName%22%3A%22ImagesGridContent%22%7D" />
    </Suspense>
  );
};

export default ImagesGridInteractive;